import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { taxCodesFor, useAccounts, useTaxCodes, type Account } from '@/lib/accounts';
import { ApiError, api } from '@/lib/api';
import { itemProblems, plainNumber } from '@/lib/item-form';
import { useContacts } from '@/lib/contacts';
import { ITEMS_PATH, type Item, type ItemKind } from '@/lib/items';
import { i18n, useLanguage, useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { cn } from '@/lib/utils';
import {
  homeCurrency,
  homeCurrencyLabel,
  isIndia,
  taxWord,
  useArabicEnabled,
} from '@/lib/org-locale';
import { ArabicField } from '@/components/arabic-field';
import { arabicValue, nameFor } from '@/lib/arabic';
import { DateInput } from '@/components/date-input';
import { todayIso } from '@/lib/dates';
import { formatAmount } from '@/lib/format';

interface Form {
  kind: ItemKind;
  name: string;
  nameAr: string;
  salesDescriptionAr: string;
  purchaseDescriptionAr: string;
  sku: string;
  isSold: boolean;
  salesDescription: string;
  salesPrice: string;
  salesAccountId: string;
  salesTaxCodeId: string;
  isPurchased: boolean;
  purchaseDescription: string;
  purchasePrice: string;
  purchaseAccountId: string;
  purchaseTaxCodeId: string;
  preferredVendorId: string;
  hsnSac: string;
  uqc: string;
  /** Inventory items (ADR-0015). */
  inventoryAssetAccountId: string;
  initialQuantity: string;
  asOfDate: string;
  reorderPoint: string;
}

function initial(item: Item | null | undefined, name = '', duplicate = false): Form {
  if (!item)
    return {
      kind: 'service',
      name,
      nameAr: '',
      salesDescriptionAr: '',
      purchaseDescriptionAr: '',
      sku: '',
      isSold: true,
      salesDescription: '',
      salesPrice: '',
      salesAccountId: '',
      salesTaxCodeId: '',
      isPurchased: false,
      purchaseDescription: '',
      purchasePrice: '',
      purchaseAccountId: '',
      purchaseTaxCodeId: '',
      preferredVendorId: '',
      hsnSac: '',
      uqc: '',
      inventoryAssetAccountId: '',
      initialQuantity: '',
      asOfDate: todayIso(),
      reorderPoint: '',
    };
  return {
    kind: item.kind,
    name: duplicate ? i18n.t('salesPages.itemDrawer.copyName', { name: item.name }) : item.name,
    nameAr: item.nameAr ?? '',
    salesDescriptionAr: item.salesDescriptionAr ?? '',
    purchaseDescriptionAr: item.purchaseDescriptionAr ?? '',
    sku: duplicate ? '' : (item.sku ?? ''),
    isSold: item.isSold,
    salesDescription: item.salesDescription ?? '',
    salesPrice: item.salesPrice ?? '',
    salesAccountId: item.salesAccountId ?? '',
    salesTaxCodeId: item.salesTaxCodeId ?? '',
    isPurchased: item.isPurchased,
    purchaseDescription: item.purchaseDescription ?? '',
    purchasePrice: item.purchasePrice ?? '',
    purchaseAccountId: item.purchaseAccountId ?? '',
    purchaseTaxCodeId: item.purchaseTaxCodeId ?? '',
    preferredVendorId: item.preferredVendorId ?? '',
    hsnSac: item.hsnSac ?? '',
    uqc: item.uqc ?? '',
    inventoryAssetAccountId: item.inventoryAssetAccountId ?? '',
    initialQuantity: '',
    asOfDate: todayIso(),
    reorderPoint: item.reorderPoint ?? '',
  };
}

/** QBO's default income account: service revenue for services, sales of goods otherwise. */
function defaultIncome(accounts: Account[], kind: ItemKind): string {
  const want = kind === 'service' ? 'service_income' : 'sales_of_product';
  return (accounts.find((a) => a.detailType === want) ?? accounts[0])?.id ?? '';
}

/** Default purchase account: cost of sales for goods, direct costs for services. */
function defaultExpense(accounts: Account[], kind: ItemKind): string {
  const want = kind === 'service' ? ['cost_of_labour', 'cost_of_sales'] : ['cost_of_sales'];
  return (
    (
      accounts.find((a) => want.includes(a.detailType)) ??
      accounts.find((a) => a.class === 'expense')
    )?.id ?? ''
  );
}

/**
 * Product/service drawer (document 06 S12; QBO's product/service information panel): type,
 * name and SKU, then a sales and a purchasing section, each switched on by its checkbox.
 */
export function ItemDrawer({
  open,
  onOpenChange,
  item,
  duplicateOf,
  initialName,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: Item | null;
  /** Prefills a new item from an existing one (list action "Duplicate"). */
  duplicateOf?: Item | null;
  initialName?: string;
  onSaved?: (item: Item) => void;
}) {
  const t = useT();
  const lang = useLanguage();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const accounts = useAccounts();
  const taxCodes = useTaxCodes();
  const vendors = useContacts({ kind: 'vendor' });
  const [form, setForm] = useState<Form>(() => initial(null));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const arabicOn = useArabicEnabled();
  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    // an edited field drops its error until the next save
    setErrors((e) => {
      if (!(k in e)) return e;
      const rest = { ...e };
      delete rest[k];
      return rest;
    });
  };

  const incomeAccounts = useMemo(
    () =>
      (accounts.data ?? []).filter(
        (a) =>
          a.class === 'income' && !a.isControl && (a.isActive || a.id === item?.salesAccountId),
      ),
    [accounts.data, item],
  );
  const expenseAccounts = useMemo(
    () =>
      (accounts.data ?? []).filter(
        (a) =>
          (a.class === 'expense' || a.class === 'asset') &&
          !a.isControl &&
          (a.isActive || a.id === item?.purchaseAccountId),
      ),
    [accounts.data, item],
  );
  const inventoryAccounts = useMemo(
    () =>
      (accounts.data ?? []).filter(
        (a) =>
          a.detailType === 'inventory' && (a.isActive || a.id === item?.inventoryAssetAccountId),
      ),
    [accounts.data, item],
  );
  const isInventory = form.kind === 'inventory';
  const salesCodes = taxCodesFor(taxCodes.data, 'sales');
  const purchaseCodes = taxCodesFor(taxCodes.data, 'purchases');

  useEffect(() => {
    if (!open) return;
    setErrors({});
    const f = duplicateOf ? initial(duplicateOf, '', true) : initial(item, initialName);
    setForm(f);
  }, [open, item, duplicateOf, initialName]);

  // A new item starts with QBO's default accounts once the chart has loaded.
  useEffect(() => {
    if (!open || item || duplicateOf) return;
    setForm((f) => ({
      ...f,
      salesAccountId: f.salesAccountId || defaultIncome(incomeAccounts, f.kind),
    }));
  }, [open, item, duplicateOf, incomeAccounts]);

  const chooseKind = (kind: ItemKind) =>
    setForm((f) => {
      if (item || duplicateOf) return { ...f, kind };
      // follow the type with the default accounts unless the user picked others
      const wasDefaultIncome = f.salesAccountId === defaultIncome(incomeAccounts, f.kind);
      const wasDefaultExpense = f.purchaseAccountId === defaultExpense(expenseAccounts, f.kind);
      const next = {
        ...f,
        kind,
        salesAccountId: wasDefaultIncome ? defaultIncome(incomeAccounts, kind) : f.salesAccountId,
        purchaseAccountId:
          wasDefaultExpense && f.purchaseAccountId
            ? defaultExpense(expenseAccounts, kind)
            : f.purchaseAccountId,
      };
      // QBO: an inventory item is sold and bought, its cost goes to cost of goods sold
      return kind === 'inventory'
        ? {
            ...next,
            isSold: true,
            isPurchased: true,
            purchaseAccountId: f.purchaseAccountId || defaultExpense(expenseAccounts, kind),
            purchaseDescription: f.purchaseDescription || f.salesDescription,
            inventoryAssetAccountId: f.inventoryAssetAccountId || (inventoryAccounts[0]?.id ?? ''),
          }
        : next;
    });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        kind: form.kind,
        name: form.name.trim(),
        // Arabic is sent only while it is on, so switching it off never clears a name
        ...(arabicOn
          ? {
              nameAr: arabicValue(form.nameAr),
              salesDescriptionAr: form.isSold ? arabicValue(form.salesDescriptionAr) : null,
              purchaseDescriptionAr: form.isPurchased
                ? arabicValue(form.purchaseDescriptionAr)
                : null,
            }
          : {}),
        sku: form.sku,
        ...(isIndia() ? { hsnSac: form.hsnSac, uqc: form.uqc } : {}),
        isSold: form.isSold,
        salesDescription: form.isSold ? form.salesDescription : '',
        salesPrice: form.isSold ? form.salesPrice : '',
        salesAccountId: form.isSold ? form.salesAccountId || null : null,
        salesTaxCodeId: form.isSold ? form.salesTaxCodeId || null : null,
        isPurchased: form.isPurchased,
        purchaseDescription: form.isPurchased ? form.purchaseDescription : '',
        purchasePrice: form.isPurchased ? form.purchasePrice : '',
        purchaseAccountId: form.isPurchased ? form.purchaseAccountId || null : null,
        purchaseTaxCodeId: form.isPurchased ? form.purchaseTaxCodeId || null : null,
        preferredVendorId: form.isPurchased ? form.preferredVendorId || null : null,
        ...(form.kind === 'inventory'
          ? {
              inventoryAssetAccountId: form.inventoryAssetAccountId || null,
              reorderPoint: form.reorderPoint.trim(),
              ...(!item && plainNumber(form.initialQuantity)
                ? { initialQuantity: plainNumber(form.initialQuantity), asOfDate: form.asOfDate }
                : {}),
            }
          : {}),
      };
      return item
        ? api<Item>(`/orgs/${orgId}/items/${item.id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          })
        : api<Item>(`/orgs/${orgId}/items`, { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ['items', orgId] });
      toast(t('salesPages.recordDrawer.saved', { name: saved.name }), {
        link: { to: ITEMS_PATH, label: t('salesPages.itemDrawer.viewAll') },
      });
      onSaved?.(saved);
      onOpenChange(false);
    },
    onError: (e) => {
      if (e instanceof ApiError && Array.isArray(e.details)) {
        const next: Record<string, string> = {};
        for (const d of e.details as Array<{ path: string | string[]; message: string }>)
          next[Array.isArray(d.path) ? d.path.join('.') : d.path] = d.message;
        setErrors(next);
      }
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next = itemProblems(form, isIndia());
    setErrors(next);
    if (!Object.keys(next).length) save.mutate();
  }

  const err = (k: string) => errors[k];
  const title = item ? t('salesPages.itemDrawer.titleEdit') : t('salesPages.itemList.newItem');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[600px]">
        <form noValidate onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>
              {item
                ? nameFor(lang, item.name, item.nameAr)
                : t('salesPages.itemDrawer.description')}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4">
            <ErrorBanner>{save.error ? save.error.message : null}</ErrorBanner>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-sm font-semibold">
                {t('salesPages.itemList.type')}
              </legend>
              <div
                className="grid grid-cols-3 gap-2"
                role="radiogroup"
                aria-label={t('salesPages.itemList.type')}
              >
                <KindCard
                  kind="service"
                  selected={form.kind === 'service'}
                  onSelect={chooseKind}
                  hint={t('salesPages.itemDrawer.kindHint.service')}
                />
                <KindCard
                  kind="non_inventory"
                  selected={form.kind === 'non_inventory'}
                  onSelect={chooseKind}
                  hint={t('salesPages.itemDrawer.kindHint.non_inventory')}
                />
                <KindCard
                  kind="inventory"
                  selected={form.kind === 'inventory'}
                  disabled={Boolean(item && item.kind !== 'inventory')}
                  onSelect={chooseKind}
                  hint={t('salesPages.itemDrawer.kindHint.inventory')}
                />
              </div>
              {err('kind') ? <p className="text-xs text-destructive">{err('kind')}</p> : null}
            </fieldset>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field
                label={t('settingsPages.data.tree.nameRequired')}
                className="col-span-2"
                error={err('name')}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.name}
                    maxLength={200}
                    autoFocus
                    aria-invalid={Boolean(err('name'))}
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              {arabicOn ? (
                <div className="col-span-2">
                  <ArabicField
                    maxLength={200}
                    value={form.nameAr}
                    error={err('nameAr')}
                    onChange={(v) => set('nameAr', v)}
                  />
                </div>
              ) : null}
              <Field label={t('salesPages.itemList.colSku')} error={err('sku')}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.sku}
                    maxLength={60}
                    aria-invalid={Boolean(err('sku'))}
                    onChange={(e) => set('sku', e.target.value)}
                  />
                )}
              </Field>
              {isIndia() ? (
                <>
                  <Field
                    label={
                      form.kind === 'service'
                        ? t('salesPages.itemDrawer.sacCode')
                        : t('salesPages.itemDrawer.hsnCode')
                    }
                    hint={
                      form.kind === 'service'
                        ? t('salesPages.itemDrawer.sacHint')
                        : t('salesPages.itemDrawer.hsnHint')
                    }
                    error={err('hsnSac')}
                  >
                    {(id) => (
                      <Input
                        id={id}
                        value={form.hsnSac}
                        inputMode="numeric"
                        aria-invalid={Boolean(err('hsnSac'))}
                        onChange={(e) => set('hsnSac', e.target.value.replace(/\s+/g, ''))}
                      />
                    )}
                  </Field>
                  {form.kind === 'service' ? null : (
                    <Field
                      label={t('salesPages.itemDrawer.uqc')}
                      hint={t('salesPages.itemDrawer.uqcHint')}
                      error={err('uqc')}
                    >
                      {(id) => (
                        <Input
                          id={id}
                          value={form.uqc}
                          maxLength={3}
                          onChange={(e) => set('uqc', e.target.value.toUpperCase())}
                        />
                      )}
                    </Field>
                  )}
                </>
              ) : null}
              <Field
                label={t('salesPages.contactDetail.field.currency')}
                hint={t('salesPages.itemDrawer.currencyHint')}
              >
                {(id) => <Input id={id} value={homeCurrencyLabel()} disabled />}
              </Field>
            </div>

            {isInventory ? (
              <Section title={t('salesPages.itemKind.inventory')} checkbox={null}>
                {item ? (
                  <Field label={t('salesPages.itemDrawer.quantityOnHand')}>
                    {(id) => (
                      <Input
                        id={id}
                        value={`${item.quantityOnHand ?? '0'} · ${homeCurrency()} ${formatAmount(item.inventoryValue ?? '0')}`}
                        disabled
                      />
                    )}
                  </Field>
                ) : (
                  <>
                    <Field
                      label={t('salesPages.itemDrawer.initialQuantity')}
                      error={err('initialQuantity')}
                    >
                      {(id) => (
                        <Input
                          id={id}
                          value={form.initialQuantity}
                          inputMode="decimal"
                          className="tnum text-end"
                          aria-invalid={Boolean(err('initialQuantity'))}
                          onChange={(e) => set('initialQuantity', e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label={t('salesPages.itemDrawer.asOfDate')} error={err('asOfDate')}>
                      {(id) => (
                        <DateInput
                          id={id}
                          value={form.asOfDate}
                          onChange={(v) => set('asOfDate', v)}
                        />
                      )}
                    </Field>
                  </>
                )}
                <Field label={t('salesPages.itemDrawer.reorderPoint')} error={err('reorderPoint')}>
                  {(id) => (
                    <Input
                      id={id}
                      value={form.reorderPoint}
                      inputMode="decimal"
                      className="tnum text-end"
                      aria-invalid={Boolean(err('reorderPoint'))}
                      onChange={(e) => set('reorderPoint', e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label={t('salesPages.itemDrawer.inventoryAssetAccount')}
                  error={err('inventoryAssetAccountId')}
                >
                  {(id) => (
                    <NativeSelect
                      id={id}
                      value={form.inventoryAssetAccountId}
                      aria-invalid={Boolean(err('inventoryAssetAccountId'))}
                      onChange={(e) => set('inventoryAssetAccountId', e.target.value)}
                    >
                      <option value="">{t('accountDrawer.chooseAccount')}</option>
                      {inventoryAccounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} · {nameFor(lang, a.fullName, a.fullNameAr)}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </Field>
                {!item ? (
                  <p className="col-span-2 text-xs text-muted-foreground">
                    {t('salesPages.itemDrawer.openingNote')}
                  </p>
                ) : null}
              </Section>
            ) : null}

            <Section
              title={t('dashboard.sales')}
              checkbox={
                <Checkbox
                  label={t('salesPages.itemDrawer.sellCheckbox')}
                  checked={form.isSold}
                  disabled={isInventory}
                  onChange={(e) => set('isSold', e.target.checked)}
                />
              }
              error={err('isSold')}
            >
              {form.isSold ? (
                <>
                  <Field
                    label={t('salesPages.itemDrawer.salesDescription')}
                    className="col-span-2"
                    error={err('salesDescription')}
                  >
                    {(id) => (
                      <Textarea
                        id={id}
                        value={form.salesDescription}
                        maxLength={1000}
                        onChange={(e) => set('salesDescription', e.target.value)}
                      />
                    )}
                  </Field>
                  {arabicOn ? (
                    <div className="col-span-2">
                      <ArabicField
                        label={t('salesPages.itemDrawer.salesDescriptionAr')}
                        what="description"
                        multiline
                        maxLength={1000}
                        value={form.salesDescriptionAr}
                        error={err('salesDescriptionAr')}
                        onChange={(v) => set('salesDescriptionAr', v)}
                      />
                    </div>
                  ) : null}
                  <Field
                    label={t('salesPages.itemDrawer.salesPrice', { currency: homeCurrency() })}
                    error={err('salesPrice')}
                  >
                    {(id) => (
                      <Input
                        id={id}
                        value={form.salesPrice}
                        inputMode="decimal"
                        className="tnum text-end"
                        aria-invalid={Boolean(err('salesPrice'))}
                        onChange={(e) => set('salesPrice', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    label={t('salesPages.itemDrawer.incomeAccount')}
                    error={err('salesAccountId')}
                  >
                    {(id) => (
                      <NativeSelect
                        id={id}
                        value={form.salesAccountId}
                        aria-invalid={Boolean(err('salesAccountId'))}
                        onChange={(e) => set('salesAccountId', e.target.value)}
                      >
                        <option value="">{t('accountDrawer.chooseAccount')}</option>
                        {incomeAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} · {nameFor(lang, a.fullName, a.fullNameAr)}
                          </option>
                        ))}
                      </NativeSelect>
                    )}
                  </Field>
                  <Field
                    label={t('salesPages.itemDrawer.salesTax', { tax: taxWord() })}
                    hint={t('salesPages.itemDrawer.salesTaxHint', { tax: taxWord() })}
                    className="col-span-2"
                    error={err('salesTaxCodeId')}
                  >
                    {(id) => (
                      <NativeSelect
                        id={id}
                        value={form.salesTaxCodeId}
                        onChange={(e) => set('salesTaxCodeId', e.target.value)}
                      >
                        <option value="">{t('salesPages.itemDrawer.fromIncomeAccount')}</option>
                        {salesCodes.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.code} · {nameFor(lang, c.name, c.nameAr)}
                          </option>
                        ))}
                        {item?.salesTaxCodeId &&
                        !salesCodes.some((c) => c.id === item.salesTaxCodeId) ? (
                          <option value={item.salesTaxCodeId}>
                            {item.salesTaxCode}
                            {t('settingsPages.data.tracking.inactiveSuffix')}
                          </option>
                        ) : null}
                      </NativeSelect>
                    )}
                  </Field>
                </>
              ) : null}
            </Section>

            <Section
              title={t('salesPages.itemDrawer.purchasing')}
              checkbox={
                <Checkbox
                  label={t('salesPages.itemDrawer.purchaseCheckbox')}
                  checked={form.isPurchased}
                  disabled={isInventory}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setForm((f) => ({
                      ...f,
                      isPurchased: on,
                      purchaseAccountId:
                        on && !f.purchaseAccountId
                          ? defaultExpense(expenseAccounts, f.kind)
                          : f.purchaseAccountId,
                      purchaseDescription:
                        on && !f.purchaseDescription ? f.salesDescription : f.purchaseDescription,
                    }));
                  }}
                />
              }
            >
              {form.isPurchased ? (
                <>
                  <Field
                    label={t('salesPages.itemDrawer.purchaseDescription')}
                    className="col-span-2"
                    error={err('purchaseDescription')}
                  >
                    {(id) => (
                      <Textarea
                        id={id}
                        value={form.purchaseDescription}
                        maxLength={1000}
                        onChange={(e) => set('purchaseDescription', e.target.value)}
                      />
                    )}
                  </Field>
                  {arabicOn ? (
                    <div className="col-span-2">
                      <ArabicField
                        label={t('salesPages.itemDrawer.purchaseDescriptionAr')}
                        what="description"
                        multiline
                        maxLength={1000}
                        value={form.purchaseDescriptionAr}
                        error={err('purchaseDescriptionAr')}
                        onChange={(v) => set('purchaseDescriptionAr', v)}
                      />
                    </div>
                  ) : null}
                  <Field
                    label={t('salesPages.itemDrawer.cost', { currency: homeCurrency() })}
                    error={err('purchasePrice')}
                  >
                    {(id) => (
                      <Input
                        id={id}
                        value={form.purchasePrice}
                        inputMode="decimal"
                        className="tnum text-end"
                        aria-invalid={Boolean(err('purchasePrice'))}
                        onChange={(e) => set('purchasePrice', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field
                    label={
                      isInventory
                        ? t('salesPages.itemDrawer.cogsAccount')
                        : t('salesPages.itemDrawer.expenseAccount')
                    }
                    error={err('purchaseAccountId')}
                  >
                    {(id) => (
                      <NativeSelect
                        id={id}
                        value={form.purchaseAccountId}
                        aria-invalid={Boolean(err('purchaseAccountId'))}
                        onChange={(e) => set('purchaseAccountId', e.target.value)}
                      >
                        <option value="">{t('accountDrawer.chooseAccount')}</option>
                        {expenseAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} · {nameFor(lang, a.fullName, a.fullNameAr)}
                          </option>
                        ))}
                      </NativeSelect>
                    )}
                  </Field>
                  <Field
                    label={t('salesPages.itemDrawer.purchaseTax', { tax: taxWord() })}
                    error={err('purchaseTaxCodeId')}
                  >
                    {(id) => (
                      <NativeSelect
                        id={id}
                        value={form.purchaseTaxCodeId}
                        onChange={(e) => set('purchaseTaxCodeId', e.target.value)}
                      >
                        <option value="">{t('salesPages.itemDrawer.fromExpenseAccount')}</option>
                        {purchaseCodes.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.code} · {nameFor(lang, c.name, c.nameAr)}
                          </option>
                        ))}
                        {item?.purchaseTaxCodeId &&
                        !purchaseCodes.some((c) => c.id === item.purchaseTaxCodeId) ? (
                          <option value={item.purchaseTaxCodeId}>
                            {item.purchaseTaxCode}
                            {t('settingsPages.data.tracking.inactiveSuffix')}
                          </option>
                        ) : null}
                      </NativeSelect>
                    )}
                  </Field>
                  <Field
                    label={t('salesPages.itemDrawer.preferredVendor')}
                    error={err('preferredVendorId')}
                  >
                    {(id) => (
                      <NativeSelect
                        id={id}
                        value={form.preferredVendorId}
                        onChange={(e) => set('preferredVendorId', e.target.value)}
                      >
                        <option value="">{t('common.none')}</option>
                        {(vendors.data ?? []).map((v) => (
                          <option key={v.id} value={v.id}>
                            {nameFor(lang, v.displayName, v.displayNameAr)}
                          </option>
                        ))}
                        {item?.preferredVendorId &&
                        !(vendors.data ?? []).some((v) => v.id === item.preferredVendorId) ? (
                          <option value={item.preferredVendorId}>{item.preferredVendorName}</option>
                        ) : null}
                      </NativeSelect>
                    )}
                  </Field>
                </>
              ) : null}
            </Section>
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            {/* the account pickers and their defaults need the chart of accounts first */}
            <Button type="submit" disabled={save.isPending || !accounts.data}>
              {save.isPending ? t('common.saving') : t('common.saveAndClose')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function KindCard({
  kind,
  selected,
  disabled,
  hint,
  onSelect,
}: {
  kind: ItemKind;
  selected: boolean;
  disabled?: boolean;
  hint: string;
  onSelect: (kind: ItemKind) => void;
}) {
  const t = useT();
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => onSelect(kind)}
      className={cn(
        'flex flex-col items-start gap-1 rounded-md border bg-card p-3 text-start text-sm',
        selected ? 'border-primary ring-2 ring-primary/30' : 'hover:border-foreground/30',
        disabled && 'cursor-not-allowed opacity-60 hover:border-border',
      )}
    >
      <span className="font-medium">{t(`salesPages.itemKind.${kind}`)}</span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </button>
  );
}

function Section({
  title,
  checkbox,
  error,
  children,
}: {
  title: string;
  checkbox: ReactNode;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-3 border-t pt-4">
      <legend className="sr-only">{title}</legend>
      <div className="text-sm font-semibold">{title}</div>
      {checkbox}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}
